import { gql } from '@/lib/api';

/**
 * OS-1716 — módulos de un tenant (contrato de OS-1721/OS-1716). Las reglas (core, dependencias,
 * presets) viven en el API: la consola muestra lo que el API calcula y nunca las reimplementa.
 */
export interface ModuleDefinition {
  key: string;
  name: string;
  description: string | null;
  dependsOn: string[];
  isCore: boolean;
  sortOrder: number;
}

export interface TenantModule {
  key: string;
  name: string;
  description: string | null;
  isCore: boolean;
  dependsOn: string[];
  enabled: boolean;
  /** manual | bootstrap | preset:<clave> | backfill:os1714 */
  source: string | null;
  enabledAt: string | null;
  disabledAt: string | null;
  /** Módulos habilitados que dependen de este: impiden deshabilitarlo. */
  requiredBy: string[];
  /** Dependencias deshabilitadas: impiden habilitarlo. */
  missingDependencies: string[];
}

export interface ModulePreset {
  key: string;
  name: string;
  /** Módulos del preset con los core incluidos. */
  modules: string[];
}

export interface ModuleSelectionCheck {
  modules: string[];
  unknown: string[];
  missingDependencies: { module: string; requires: string }[];
  /** Mensaje del API listo para mostrar; `null` si la selección es válida. */
  message: string | null;
}

export interface ModuleImpact {
  moduleKey: string;
  activeUsers: number;
  activeOperationalUsers: number;
}

export interface ModuleHistoryEntry {
  id: string;
  moduleKey: string;
  enabled: boolean;
  previousEnabled: boolean | null;
  reason: string | null;
  preset: string | null;
  adminEmail: string | null;
  createdAt: string;
}

export interface ModuleHistoryPage {
  entries: ModuleHistoryEntry[];
  nextCursor: string | null;
}

/** Debe coincidir con `MODULE_REASON_MIN_LENGTH` / `MAX_LENGTH` del API (el API valida igual). */
export const MODULE_REASON_LIMITS = { minLength: 10, maxLength: 500 } as const;

const TENANT_MODULE_FIELDS = `
  key name description isCore dependsOn enabled source enabledAt disabledAt
  requiredBy missingDependencies
`;

const CATALOG = `
  query PlatformModuleCatalog {
    platformModuleCatalog { key name description dependsOn isCore sortOrder }
  }
`;

const PRESETS = `
  query PlatformModulePresets {
    platformModulePresets { key name modules }
  }
`;

const SELECTION_CHECK = `
  query PlatformModuleSelectionCheck($modules: [String!]!) {
    platformModuleSelectionCheck(modules: $modules) {
      modules unknown message
      missingDependencies { module requires }
    }
  }
`;

const TENANT_MODULES = `
  query PlatformTenantModules($tenantId: ID!) {
    platformTenantModules(tenantId: $tenantId) { ${TENANT_MODULE_FIELDS} }
  }
`;

const IMPACT = `
  query PlatformTenantModuleImpact($tenantId: ID!, $moduleKey: String!) {
    platformTenantModuleImpact(tenantId: $tenantId, moduleKey: $moduleKey) {
      moduleKey activeUsers activeOperationalUsers
    }
  }
`;

const HISTORY = `
  query PlatformTenantModuleHistory($tenantId: ID!, $take: Int, $cursor: String, $moduleKey: String) {
    platformTenantModuleHistory(
      tenantId: $tenantId
      take: $take
      cursor: $cursor
      moduleKey: $moduleKey
    ) {
      entries { id moduleKey enabled previousEnabled reason preset adminEmail createdAt }
      nextCursor
    }
  }
`;

const SET_MODULE = `
  mutation SetPlatformTenantModule($input: SetPlatformTenantModuleInput!) {
    setPlatformTenantModule(input: $input) { ${TENANT_MODULE_FIELDS} }
  }
`;

const SET_MODULES = `
  mutation SetPlatformTenantModules($input: SetPlatformTenantModulesInput!) {
    setPlatformTenantModules(input: $input) { ${TENANT_MODULE_FIELDS} }
  }
`;

export async function getModuleCatalog(): Promise<ModuleDefinition[]> {
  const data = await gql<{ platformModuleCatalog: ModuleDefinition[] }>(CATALOG);
  return data.platformModuleCatalog;
}

export async function getModulePresets(): Promise<ModulePreset[]> {
  const data = await gql<{ platformModulePresets: ModulePreset[] }>(PRESETS);
  return data.platformModulePresets;
}

export async function checkModuleSelection(modules: string[]): Promise<ModuleSelectionCheck> {
  const data = await gql<{ platformModuleSelectionCheck: ModuleSelectionCheck }>(SELECTION_CHECK, {
    modules,
  });
  return data.platformModuleSelectionCheck;
}

export async function getTenantModules(tenantId: string): Promise<TenantModule[]> {
  const data = await gql<{ platformTenantModules: TenantModule[] }>(TENANT_MODULES, { tenantId });
  return data.platformTenantModules;
}

export async function getModuleImpact(tenantId: string, moduleKey: string): Promise<ModuleImpact> {
  const data = await gql<{ platformTenantModuleImpact: ModuleImpact }>(IMPACT, {
    tenantId,
    moduleKey,
  });
  return data.platformTenantModuleImpact;
}

export async function getModuleHistory(
  tenantId: string,
  options: { take?: number; cursor?: string | null; moduleKey?: string | null } = {},
): Promise<ModuleHistoryPage> {
  const data = await gql<{ platformTenantModuleHistory: ModuleHistoryPage }>(HISTORY, {
    tenantId,
    take: options.take ?? null,
    cursor: options.cursor ?? null,
    moduleKey: options.moduleKey ?? null,
  });
  return data.platformTenantModuleHistory;
}

/** Habilita o deshabilita un módulo. `reason` es obligatorio (queda en la auditoría). */
export async function setTenantModule(input: {
  tenantId: string;
  moduleKey: string;
  enabled: boolean;
  reason: string;
}): Promise<TenantModule[]> {
  const data = await gql<{ setPlatformTenantModule: TenantModule[] }>(SET_MODULE, { input });
  return data.setPlatformTenantModule;
}

/** Deja habilitados exactamente `modules` (+ core). `preset` solo registra de dónde salió. */
export async function setTenantModules(input: {
  tenantId: string;
  modules: string[];
  preset?: string | null;
  reason: string;
}): Promise<TenantModule[]> {
  const data = await gql<{ setPlatformTenantModules: TenantModule[] }>(SET_MODULES, { input });
  return data.setPlatformTenantModules;
}

export const tenantModulesQueryKey = (tenantId: string): unknown[] => [
  'platform-tenant-modules',
  tenantId,
];
export const moduleHistoryQueryKey = (tenantId: string, moduleKey: string | null): unknown[] => [
  'platform-tenant-module-history',
  tenantId,
  moduleKey,
];
